import { ChangeDetectionStrategy, Component } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { DsDialogRef } from 'src/app/shared/components/ds-dialog/ds-dialog.component';
import { DsButtonComponent } from 'src/app/shared/components/ds-button/ds-button.component';

/**
 * Kết quả của popup mời chuyển giao diện.
 * - 'mobile'   → người dùng chọn sang `/mobile`
 * - 'stay'     → người dùng chọn ở lại desktop (GHI NHỚ, không hỏi lại)
 * - undefined  → đóng bằng ESC / backdrop (KHÔNG ghi nhớ; chỉ tắt latch của lần thu nhỏ này)
 */
export type MobileViewPromptResult = 'mobile' | 'stay';

/**
 * Popup mời chuyển sang giao diện mobile khi viewport co xuống cỡ điện thoại (< 768px).
 *
 * App có HAI giao diện tách bạch: shell desktop (`/`) và shell mobile (`/mobile`, epic
 * MOBILE-01). Shell desktop KHÔNG còn nhánh responsive xuống cỡ điện thoại — dưới 768px
 * nó giữ nguyên bố cục (min-width) và cuộn ngang, còn popup này mời sang bản mobile.
 *
 * Điều phối (ngưỡng, debounce, latch, ghi nhớ) nằm ở `MobileViewPromptService`.
 * Icon chỉ dùng glyph có trong subset tự host (`devices`, `arrow_right_alt`) — thêm tên
 * ngoài subset sẽ làm đỏ cổng `npm run icons:check` và render ra chữ thô.
 */
@Component({
  selector: 'mobile-view-prompt-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, DsButtonComponent],
  template: `
    <div class="p-5 flex flex-col gap-4">
      <div class="flex items-start gap-3">
        <span
          class="material-symbols-outlined icon-size-token-lg text-warm-secondary flex-shrink-0"
          aria-hidden="true"
        >
          devices
        </span>
        <div class="flex flex-col gap-1 min-w-0">
          <h2 class="text-token-heading-xs font-semibold text-warm-text m-0">
            {{ 'Layout.MobileViewPrompt.Title' | translate }}
          </h2>
          <p class="text-token-body-dense text-warm-secondary m-0">
            {{ 'Layout.MobileViewPrompt.Message' | translate }}
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <ds-button
          variant="primary"
          size="md"
          [icon]="'arrow_right_alt'"
          (click)="choose('mobile')"
        >
          {{ 'Layout.MobileViewPrompt.Switch' | translate }}
        </ds-button>
        <ds-button variant="ghost" size="md" (click)="choose('stay')">
          {{ 'Layout.MobileViewPrompt.Stay' | translate }}
        </ds-button>
      </div>

      <p class="text-token-label text-warm-hint m-0 text-center">
        {{ 'Layout.MobileViewPrompt.Hint' | translate }}
      </p>
    </div>
  `,
})
export class MobileViewPromptDialogComponent {
  constructor(
    private readonly _dialogRef: DsDialogRef<
      MobileViewPromptDialogComponent,
      MobileViewPromptResult
    >,
  ) {}

  choose(result: MobileViewPromptResult): void {
    this._dialogRef.close(result);
  }
}
